import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes, ThemeProvider } from "styled-components";
import {
  empGetMe, empGetStats, empGetActivity,
  empGetTickets, empGetTicket, empUpdateTicket, empPostMessage, empSelfAssign,
  empGetComments, empDeleteComment,
  empGetWithdrawals, empApproveWithdrawal, empRejectWithdrawal,
  empGetPayouts, empPayoutAction,
  empGetUsers,
} from "./employeeClient";
import EmployeeSupportTickets from "./EmployeeSupportTickets";
import { useNotificationSocket } from "./useNotificationSocket";

/* ─── Theme ──────────────────────────────────────────────────────────────── */
const theme = {
  bg:"#080a0c", bgCard:"#0e1115", bgHover:"#141820",
  border:"#1e2530", borderLight:"#2a3340",
  green:"#00e676", greenDim:"#00e67622",
  red:"#ff3b5c",   redDim:"#ff3b5c22",
  gold:"#ffc107",  accent:"#4fc3f7",
  blue:"#1976d2",  blueDim:"rgba(25,118,210,.15)",
  text:"#e8edf2",  textMuted:"#5a6a7a", textDim:"#8a9aaa",
  fontMono:"'DM Mono','Courier New',monospace",
  fontDisplay:"'Playfair Display',Georgia,serif",
  fontBody:"'Syne',sans-serif",
};

/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeUp  = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;

/* ─── Role config ────────────────────────────────────────────────────────── */
const ROLE_CONFIG = {
  support:    { label:"Support Client",  color:"#4fc3f7", icon:"🎧", tabs:["overview","tickets","users"] },
  moderator:  { label:"Modérateur",      color:"#9c27b0", icon:"🛡", tabs:["overview","moderation"] },
  finance:    { label:"Finance",         color:"#00e676", icon:"💰", tabs:["overview","withdrawals","payouts"] },
  manager:    { label:"Manager",         color:"#ffc107", icon:"📊", tabs:["overview","tickets","users"] },
  compliance: { label:"Compliance",      color:"#ff9800", icon:"⚖",  tabs:["overview","users","withdrawals","payouts","activity"] },
  analyst:    { label:"Analyste",        color:"#e91e63", icon:"📈", tabs:["overview","activity"] },
  staff:      { label:"Staff",           color:"#8a9aaa", icon:"👤", tabs:["overview","tickets","moderation","withdrawals","payouts","users","activity"] },
};

const TAB_LABELS = {
  overview:    { icon:"◉", label:"Vue d'ensemble" },
  tickets:     { icon:"🎫", label:"Tickets" },
  moderation:  { icon:"🛡", label:"Modération" },
  withdrawals: { icon:"💸", label:"Retraits" },
  payouts:     { icon:"💳", label:"Payouts" },
  users:       { icon:"👥", label:"Utilisateurs" },
  activity:    { icon:"📋", label:"Activité" },
};

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Shell    = styled.div`min-height:100vh;background:${t=>t.theme.bg};color:${t=>t.theme.text};font-family:${t=>t.theme.fontBody};display:flex;`;
const Sidebar  = styled.aside`
  width:220px;min-height:100vh;background:${t=>t.theme.bgCard};
  border-right:1px solid ${t=>t.theme.border};
  display:flex;flex-direction:column;flex-shrink:0;
  position:sticky;top:0;height:100vh;overflow-y:auto;
  z-index:150;transition:transform .25s ease;

  @media(min-width:769px){
    transform:${({$collapsed})=>$collapsed?"translateX(-220px)":"translateX(0)"};
    margin-left:${({$collapsed})=>$collapsed?"-220px":"0"};
    transition:transform .25s ease,margin-left .25s ease;
  }
  @media(max-width:768px){
    position:fixed;top:0;left:0;bottom:0;
    transform:${({$open})=>$open?"translateX(0)":"translateX(-100%)"};
    box-shadow:${({$open})=>$open?"4px 0 24px rgba(0,0,0,.5)":"none"};
  }
`;
const SideOverlay = styled.div`
  display:none;
  @media(max-width:768px){
    display:${({$open})=>$open?"block":"none"};
    position:fixed;inset:0;background:rgba(0,0,0,.6);
    z-index:149;animation:${fadeUp} .2s ease;
  }
`;
const ToggleBtn = styled.button`
  position:fixed;top:.85rem;
  left:${({$collapsed,$open})=>(!$collapsed||$open)?"228px":"1rem"};
  z-index:200;width:32px;height:32px;border-radius:8px;
  border:1px solid ${t=>t.theme.border};background:${t=>t.theme.bgCard};
  color:${t=>t.theme.textMuted};font-size:.85rem;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:left .25s ease,color .15s,border-color .15s;
  box-shadow:0 2px 8px rgba(0,0,0,.3);
  &:hover{color:${t=>t.theme.text};border-color:${t=>t.theme.borderLight};}
  @media(max-width:768px){
    left:${({$open})=>$open?"228px":"1rem"};top:.85rem;
  }
`;
const SideTop  = styled.div`padding:1.25rem 1rem;border-bottom:1px solid ${t=>t.theme.border};`;
const SideLogo = styled.div`font-family:${t=>t.theme.fontDisplay};font-size:1.1rem;font-weight:700; cursor: pointer; letter-spacing:-.02em;margin-bottom:1rem;span{color:${t=>t.theme.green};}`;
const RoleBadge= styled.div`display:inline-flex;align-items:center;gap:.35rem;padding:.25rem .6rem;border-radius:100px;font-family:${t=>t.theme.fontMono};font-size:.62rem;letter-spacing:.06em;background:${t=>`${t.$color}18`};color:${t=>t.$color};border:1px solid ${t=>`${t.$color}44`};`;
const Nav      = styled.nav`padding:.75rem 0;flex:1;`;
const NavItem  = styled.button`width:100%;display:flex;align-items:center;gap:.65rem;padding:.65rem 1rem;background:none;border:none;font-family:${t=>t.theme.fontBody};font-size:.8rem;font-weight:500;cursor:pointer;transition:all .15s;text-align:left;border-left:2px solid ${t=>t.$active?t.theme.accent:"transparent"};background:${t=>t.$active?"rgba(79,195,247,.06)":"transparent"};color:${t=>t.$active?t.theme.text:t.theme.textDim};&:hover{color:${t=>t.theme.text};background:${t=>t.theme.bgHover};}`;
const NavIcon  = styled.span`font-size:1rem;width:18px;text-align:center;`;
const NavBadge = styled.span`margin-left:auto;font-family:${t=>t.theme.fontMono};font-size:.58rem;background:${t=>t.theme.red};color:#fff;border-radius:100px;padding:.1rem .45rem;`;
const SideFoot = styled.div`padding:.85rem 1rem;border-top:1px solid ${t=>t.theme.border};`;
const UserChip = styled.div`display:flex;align-items:center;gap:.5rem;margin-bottom:.5rem;`;
const Avatar   = styled.div`width:32px;height:32px;border-radius:50%;background:${t=>t.$color||t.theme.bgHover};border:1px solid ${t=>t.theme.border};display:flex;align-items:center;justify-content:center;font-family:${t=>t.theme.fontMono};font-size:.68rem;font-weight:600;flex-shrink:0;`;
const SignOut  = styled.button`width:100%;padding:.4rem;border-radius:6px;background:transparent;border:1px solid ${t=>t.theme.border};color:${t=>t.theme.textMuted};font-size:.72rem;cursor:pointer;font-family:${t=>t.theme.fontBody};transition:all .15s;&:hover{border-color:${t=>t.theme.red}55;color:${t=>t.theme.red};}`;
const Main     = styled.main`flex:1;padding:2rem;overflow-x:hidden;`;

/* ─── Common ─────────────────────────────────────────────────────────────── */
const PageTitle= styled.h1`font-family:${t=>t.theme.fontDisplay};font-size:1.5rem;font-weight:700;letter-spacing:-.03em;margin-bottom:.25rem;span{color:${t=>t.theme.accent};}`;
const PageSub  = styled.p`font-size:.78rem;color:${t=>t.theme.textMuted};margin-bottom:1.75rem;`;
const StatGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fill,minmax(160px,1fr));gap:1rem;margin-bottom:2rem;`;
const StatCard = styled.div`background:${t=>t.theme.bgCard};border:1px solid ${t=>t.theme.border};border-radius:12px;padding:1rem 1.25rem;animation:${fadeUp} .4s ease both;animation-delay:${t=>t.$i*.06}s;`;
const StatVal  = styled.div`font-family:${t=>t.theme.fontMono};font-size:1.1rem;font-weight:500;color:${t=>t.$c?t.theme[t.$c]:t.theme.text};`;
const StatLbl  = styled.div`font-size:.65rem;text-transform:uppercase;letter-spacing:.08em;color:${t=>t.theme.textMuted};margin-top:.2rem;`;
const Panel    = styled.div`background:${t=>t.theme.bgCard};border:1px solid ${t=>t.theme.border};border-radius:12px;overflow:hidden;margin-bottom:1.5rem;`;
const PHead    = styled.div`display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;padding:.85rem 1.25rem;background:${t=>t.theme.bg};border-bottom:1px solid ${t=>t.theme.border};`;
const PTitle   = styled.span`font-family:${t=>t.theme.fontMono};font-size:.67rem;text-transform:uppercase;letter-spacing:.08em;color:${t=>t.theme.textMuted};`;
const PActions = styled.div`display:flex;gap:.5rem;align-items:center;flex-wrap:wrap;`;
const THead    = styled.div`display:grid;grid-template-columns:${t=>t.$cols};gap:.5rem;padding:.6rem 1.25rem;background:${t=>t.theme.bg};border-bottom:1px solid ${t=>t.theme.border};`;
const TH       = styled.span`font-family:${t=>t.theme.fontMono};font-size:.6rem;text-transform:uppercase;letter-spacing:.08em;color:${t=>t.theme.textMuted};`;
const TRow     = styled.div`display:grid;grid-template-columns:${t=>t.$cols};gap:.5rem;padding:.85rem 1.25rem;border-bottom:1px solid ${t=>t.theme.border};align-items:center;transition:background .15s;cursor:${t=>t.$click?"pointer":"default"};&:last-child{border-bottom:none;}&:hover{background:${t=>t.theme.bgHover};}`;
const Cell     = styled.div`font-size:.78rem;color:${t=>t.$dim?t.theme.textDim:t.theme.text};font-family:${t=>t.$mono?t.theme.fontMono:"inherit"};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
const Pill     = styled.span`font-family:${t=>t.theme.fontMono};font-size:.6rem;padding:.18rem .55rem;border-radius:100px;letter-spacing:.05em;background:${t=>t.$s==="open"||t.$s==="OPEN"||t.$s==="APPROVED"?t.theme.greenDim:t.$s==="in_progress"||t.$s==="PENDING"?"rgba(255,193,7,.1)":t.$s==="resolved"||t.$s==="COMPLETED"?"rgba(79,195,247,.1)":t.$s==="urgent"||t.$s==="REJECTED"?t.theme.redDim:t.theme.bgHover};color:${t=>t.$s==="open"||t.$s==="OPEN"||t.$s==="APPROVED"?t.theme.green:t.$s==="in_progress"||t.$s==="PENDING"?t.theme.gold:t.$s==="resolved"||t.$s==="COMPLETED"?t.theme.accent:t.$s==="urgent"||t.$s==="REJECTED"?t.theme.red:t.theme.textMuted};`;
const PriorityDot=styled.span`width:8px;height:8px;border-radius:50%;background:${t=>t.$p==="urgent"?"#ff3b5c":t.$p==="high"?"#ff9800":t.$p==="medium"?"#ffc107":"#5a6a7a"};display:inline-block;flex-shrink:0;`;
const Btn      = styled.button`font-family:${t=>t.theme.fontMono};font-size:.63rem;padding:.25rem .65rem;border-radius:5px;cursor:pointer;letter-spacing:.04em;transition:all .15s;white-space:nowrap;border:1px solid ${t=>t.$t==="ok"?t.theme.green+"55":t.$t==="danger"?t.theme.red+"55":t.theme.borderLight};background:${t=>t.$t==="ok"?t.theme.greenDim:t.$t==="danger"?t.theme.redDim:t.theme.bgHover};color:${t=>t.$t==="ok"?t.theme.green:t.$t==="danger"?t.theme.red:t.theme.textDim};&:disabled{opacity:.35;cursor:not-allowed;}&:hover:not(:disabled){opacity:.8;}`;
const BtnRow   = styled.div`display:flex;gap:.35rem;flex-wrap:wrap;`;
const Search   = styled.input`background:${t=>t.theme.bg};border:1px solid ${t=>t.theme.border};border-radius:7px;color:${t=>t.theme.text};font-family:${t=>t.theme.fontBody};font-size:.75rem;padding:.35rem .75rem;outline:none;width:180px;&:focus{border-color:${t=>t.theme.borderLight};}&::placeholder{color:${t=>t.theme.textMuted};}`;
const FilterChip=styled.button`font-family:${t=>t.theme.fontMono};font-size:.62rem;padding:.22rem .6rem;border-radius:100px;letter-spacing:.05em;cursor:pointer;transition:all .15s;border:1px solid ${t=>t.$active?t.theme.borderLight:t.theme.border};background:${t=>t.$active?t.theme.bgHover:"transparent"};color:${t=>t.$active?t.theme.text:t.theme.textMuted};&:hover{border-color:${t=>t.theme.borderLight};}`;
const Empty    = styled.div`text-align:center;padding:2.5rem;font-size:.8rem;color:${t=>t.theme.textMuted};`;
const Skel     = styled.div`height:48px;margin:.3rem 1.25rem;border-radius:6px;background:linear-gradient(90deg,${t=>t.theme.bgCard} 25%,${t=>t.theme.bgHover} 50%,${t=>t.theme.bgCard} 75%);background-size:400px 100%;animation:${shimmer} 1.4s infinite;`;

/* ─── Toast ──────────────────────────────────────────────────────────────── */
const ToastWrap= styled.div`position:fixed;bottom:1.5rem;right:1.5rem;z-index:9999;display:flex;flex-direction:column;gap:.5rem;`;
const ToastItem= styled.div`background:${t=>t.theme.bgCard};border:1px solid ${t=>t.$t==="ok"?t.theme.green+"55":t.$t==="err"?t.theme.red+"55":t.theme.borderLight};border-radius:10px;padding:.75rem 1rem;font-size:.78rem;color:${t=>t.$t==="ok"?t.theme.green:t.$t==="err"?t.theme.red:t.theme.text};box-shadow:0 6px 24px rgba(0,0,0,.4);min-width:240px;animation:${fadeUp} .2s ease;`;
let _tid=0;
function useToasts(){const[list,setList]=useState([]);const push=useCallback((msg,t="ok")=>{const id=++_tid;setList(p=>[...p.slice(-3),{id,msg,t}]);setTimeout(()=>setList(p=>p.filter(x=>x.id!==id)),3500);},[]);return{list,ok:m=>push(m,"ok"),err:m=>push(m,"err"),info:m=>push(m,"info")};}

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function fmt(iso){if(!iso)return"—";return new Date(iso).toLocaleDateString("fr-FR",{day:"2-digit",month:"short",year:"numeric"});}
function timeAgo(iso){const d=(Date.now()-new Date(iso))/1000;if(d<60)return"à l'instant";if(d<3600)return`il y a ${Math.floor(d/60)}m`;if(d<86400)return`il y a ${Math.floor(d/3600)}h`;return fmt(iso);}
function initials(n=""){return n.slice(0,2).toUpperCase();}

/* ─── Mock data ──────────────────────────────────────────────────────────── */
const MOCK_STATS = {tickets_assigned:12,tickets_open:5,tickets_resolved_today:3,pending_withdrawals:4,open_markets:8,total_users:412,new_users_today:7,my_actions_today:14};
const MOCK_TICKETS = [
  {id:"t1",subject:"Dépôt non crédité depuis 2h",category:"deposit",priority:"high",status:"open",user:"alice",assigned_to:null,created_at:"2026-03-17T08:00:00Z",message_count:0},
  {id:"t2",subject:"Impossible de retirer mes fonds",category:"withdrawal",priority:"urgent",status:"in_progress",user:"bob",assigned_to:"moi",created_at:"2026-03-17T07:30:00Z",message_count:2},
  {id:"t3",subject:"Mon compte a été suspendu",category:"account",priority:"medium",status:"open",user:"carol",assigned_to:null,created_at:"2026-03-16T15:00:00Z",message_count:1},
  {id:"t4",subject:"Bug sur la page de trading",category:"bug",priority:"low",status:"resolved",user:"dave",assigned_to:"moi",created_at:"2026-03-15T11:00:00Z",message_count:4},
];
const MOCK_WITHDRAWALS = [
  {id:"w1",user:"alice",coin:"EVP",amount:"500.00",address:"0x3f5C…bE",status:"PENDING",created_at:"2026-03-17T09:00:00Z"},
  {id:"w2",user:"bob",coin:"ETH",amount:"0.85",address:"0xd8dA…45",status:"PENDING",created_at:"2026-03-17T07:00:00Z"},
  {id:"w3",user:"charlie",coin:"EVP",amount:"1200.00",address:"0x742d…72",status:"APPROVED",created_at:"2026-03-16T14:00:00Z"},
];
const MOCK_COMMENTS = [
  {id:1,author:"alice",content:"Le marché devrait résoudre YES clairement.",market:"Will Bitcoin exceed $100K?",created_at:"2026-03-17T10:00:00Z"},
  {id:2,author:"spammer123",content:"GAGNER DE L'ARGENT VITE !!! visitez mon-site.com",market:"Will GPT-5 release?",created_at:"2026-03-17T09:30:00Z"},
  {id:3,author:"bob",content:"Les probabilités sont trop basses selon moi.",market:"Fed rate cut Q1 2026?",created_at:"2026-03-17T08:00:00Z"},
];
const MOCK_USERS = [
  {id:1,username:"alice",email:"alice@ex.com",is_active:true,date_joined:"2026-01-10",balance:"1240.00"},
  {id:2,username:"bob",email:"bob@ex.com",is_active:true,date_joined:"2026-01-22",balance:"320.50"},
  {id:3,username:"spammer123",email:"spam@spam.com",is_active:true,date_joined:"2026-03-16",balance:"0.00"},
];

/* ════ TICKET DETAIL ════════════════════════════════════════════════════════ */
function TicketDetail({ ticketId, onBack, toast, empRole }) {
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState("");
  const [internal,setInternal]= useState(false);
  const [sending, setSending] = useState(false);

  useEffect(()=>{
    empGetTicket(ticketId)
      .then(r=>setTicket(r.data))
      .catch(()=>setTicket({
        id:ticketId,subject:"Dépôt non crédité depuis 2h",description:"L'utilisateur indique avoir effectué un dépôt EVP de 500$ il y a 2h mais son solde n'a pas été mis à jour.",
        category:"deposit",priority:"high",status:"in_progress",
        user:{id:1,username:"alice",email:"alice@ex.com"},
        assigned_to:"support1",created_at:"2026-03-17T08:00:00Z",resolved_at:null,
        messages:[
          {id:1,author:"alice",content:"Bonjour, j'ai fait un dépôt de 500 EVP il y a 2 heures et mon solde n'a pas changé. Transaction hash: 0xabc123...",is_internal:false,is_employee:false,created_at:"2026-03-17T08:00:00Z"},
          {id:2,author:"support1",content:"Bonjour Alice, nous avons bien reçu votre demande. Pouvez-vous nous confirmer le réseau utilisé pour le transfert ?",is_internal:false,is_employee:true,created_at:"2026-03-17T08:15:00Z"},
          {id:3,author:"support1",content:"Note interne: vérifier côté wallet provider si la transaction est détectée.",is_internal:true,is_employee:true,created_at:"2026-03-17T08:16:00Z"},
        ],
      }))
      .finally(()=>setLoading(false));
  },[ticketId]);

  const sendMessage=async()=>{
    if(!reply.trim())return;
    setSending(true);
    try{await empPostMessage(ticketId,{content:reply,is_internal:internal});}
    catch{}
    setTicket(t=>({...t,messages:[...t.messages,{id:Date.now(),author:"moi",content:reply,is_internal:internal,is_employee:true,created_at:new Date().toISOString()}]}));
    setReply("");setSending(false);
    toast.ok("Message envoyé.");
  };

  const changeStatus=async(s)=>{
    try{await empUpdateTicket(ticketId,{status:s});}catch{}
    setTicket(t=>({...t,status:s}));
    toast.ok(`Statut mis à jour : ${s}`);
  };

  if(loading)return <div style={{padding:"2rem",color:"#5a6a7a"}}>Chargement…</div>;
  if(!ticket) return <div style={{padding:"2rem",color:"#5a6a7a"}}>Ticket introuvable.</div>;

  const BackBtn=styled.button`background:none;border:none;color:${t=>t.theme.textMuted};font-size:.8rem;cursor:pointer;margin-bottom:1.25rem;display:flex;align-items:center;gap:.4rem;padding:0;&:hover{color:${t=>t.theme.text};}`;
  const MsgList=styled.div`display:flex;flex-direction:column;gap:.75rem;max-height:400px;overflow-y:auto;padding-right:.25rem;`;
  const Msg=styled.div`display:flex;gap:.6rem;align-items:flex-start;`;
  const MsgBubble=styled.div`flex:1;background:${t=>t.$internal?"rgba(255,193,7,.06)":t.$emp?t.theme.bgHover:t.theme.bg};border:1px solid ${t=>t.$internal?t.theme.gold+"33":t.theme.border};border-radius:${t=>t.$emp?"12px 12px 2px 12px":"2px 12px 12px 12px"};padding:.65rem .85rem;`;
  const MsgAuthor=styled.div`font-size:.7rem;font-weight:600;color:${t=>t.$emp?t.theme.accent:t.theme.text};margin-bottom:.25rem;display:flex;align-items:center;gap:.5rem;`;
  const MsgText=styled.p`font-size:.8rem;line-height:1.5;color:${t=>t.theme.text};`;
  const InternalTag=styled.span`font-family:${t=>t.theme.fontMono};font-size:.55rem;padding:.1rem .4rem;border-radius:3px;background:rgba(255,193,7,.15);color:${t=>t.theme.gold};`;
  const Textarea=styled.textarea`width:100%;background:${t=>t.theme.bg};border:1px solid ${t=>t.theme.border};border-radius:8px;color:${t=>t.theme.text};font-family:${t=>t.theme.fontBody};font-size:.82rem;padding:.6rem .8rem;outline:none;resize:none;min-height:80px;transition:border-color .15s;&:focus{border-color:${t=>t.theme.borderLight};}&::placeholder{color:${t=>t.theme.textMuted};}`;
  const InternalToggle=styled.label`display:flex;align-items:center;gap:.4rem;font-size:.72rem;color:${t=>t.theme.textMuted};cursor:pointer;input{accent-color:${t=>t.theme.gold};}`;

  return(
    <div>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#5a6a7a",fontSize:".8rem",cursor:"pointer",marginBottom:"1.25rem",display:"flex",alignItems:"center",gap:".4rem",padding:0}}>
        ← Retour aux tickets
      </button>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:"1.5rem",flexWrap:"wrap",gap:"1rem"}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:".5rem",marginBottom:".35rem"}}>
            <PriorityDot $p={ticket.priority}/>
            <h2 style={{fontFamily:"var(--font-display)",fontSize:"1.2rem",fontWeight:700}}>{ticket.subject}</h2>
          </div>
          <div style={{display:"flex",gap:".5rem",flexWrap:"wrap"}}>
            <Pill $s={ticket.status}>{ticket.status}</Pill>
            <Pill $s={ticket.priority}>{ticket.priority}</Pill>
            <span style={{fontSize:".72rem",color:"#5a6a7a",fontFamily:"DM Mono"}}>#{String(ticket.id).slice(0,8)}</span>
          </div>
        </div>
        <BtnRow>
          {ticket.status==="open"&&<Btn $t="ok" onClick={()=>changeStatus("in_progress")}>Prendre en charge</Btn>}
          {ticket.status!=="resolved"&&<Btn $t="ok" onClick={()=>changeStatus("resolved")}>✓ Résoudre</Btn>}
          {ticket.status!=="closed"&&<Btn onClick={()=>changeStatus("closed")}>Fermer</Btn>}
        </BtnRow>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 260px",gap:"1.25rem"}}>
        {/* Messages */}
        <Panel>
          <PHead><PTitle>Conversation</PTitle></PHead>
          <div style={{padding:"1.25rem",display:"flex",flexDirection:"column",gap:".75rem",maxHeight:"400px",overflowY:"auto"}}>
            {/* Description initiale */}
            <div style={{background:"rgba(79,195,247,.06)",border:"1px solid rgba(79,195,247,.2)",borderRadius:"8px",padding:".85rem 1rem"}}>
              <div style={{fontSize:".7rem",color:"#4fc3f7",marginBottom:".35rem",fontWeight:600}}>Description initiale</div>
              <p style={{fontSize:".82rem",lineHeight:1.55}}>{ticket.description}</p>
            </div>
            {/* Messages */}
            {(ticket.messages||[]).map(m=>(
              <div key={m.id} style={{display:"flex",gap:".6rem",alignItems:"flex-start",flexDirection:m.is_employee?"row-reverse":"row"}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:m.is_employee?"#1976d2":"#1e2530",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"DM Mono",fontSize:".6rem",fontWeight:600,flexShrink:0}}>
                  {initials(m.author)}
                </div>
                <div style={{flex:1,background:m.is_internal?"rgba(255,193,7,.06)":m.is_employee?"#141820":"#0e1115",border:`1px solid ${m.is_internal?"rgba(255,193,7,.2)":"#1e2530"}`,borderRadius:m.is_employee?"12px 2px 12px 12px":"2px 12px 12px 12px",padding:".6rem .8rem",maxWidth:"85%"}}>
                  <div style={{fontSize:".7rem",fontWeight:600,color:m.is_employee?"#4fc3f7":"#e8edf2",marginBottom:".2rem",display:"flex",alignItems:"center",gap:".4rem"}}>
                    {m.author}
                    {m.is_internal&&<span style={{fontFamily:"DM Mono",fontSize:".55rem",padding:".1rem .4rem",borderRadius:"3px",background:"rgba(255,193,7,.15)",color:"#ffc107"}}>NOTE INTERNE</span>}
                    <span style={{fontFamily:"DM Mono",fontSize:".58rem",color:"#5a6a7a",marginLeft:"auto"}}>{timeAgo(m.created_at)}</span>
                  </div>
                  <p style={{fontSize:".8rem",lineHeight:1.5}}>{m.content}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Reply */}
          <div style={{padding:"1rem 1.25rem",borderTop:"1px solid #1e2530",display:"flex",flexDirection:"column",gap:".6rem"}}>
            <textarea
              placeholder="Votre réponse…"
              value={reply}
              onChange={e=>setReply(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&e.ctrlKey&&sendMessage()}
              style={{width:"100%",background:"#080a0c",border:"1px solid #1e2530",borderRadius:"8px",color:"#e8edf2",fontFamily:"Syne,sans-serif",fontSize:".82rem",padding:".6rem .8rem",outline:"none",resize:"none",minHeight:"70px"}}
            />
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <label style={{display:"flex",alignItems:"center",gap:".4rem",fontSize:".72rem",color:"#5a6a7a",cursor:"pointer"}}>
                <input type="checkbox" checked={internal} onChange={e=>setInternal(e.target.checked)} style={{accentColor:"#ffc107"}}/>
                Note interne (invisible pour l'utilisateur)
              </label>
              <Btn $t="ok" disabled={!reply.trim()||sending} onClick={sendMessage}>
                {sending?"Envoi…":"Envoyer ↵"}
              </Btn>
            </div>
          </div>
        </Panel>

        {/* Infos */}
        <div style={{display:"flex",flexDirection:"column",gap:"1rem"}}>
          <Panel>
            <PHead><PTitle>Utilisateur</PTitle></PHead>
            <div style={{padding:"1rem"}}>
              <div style={{display:"flex",alignItems:"center",gap:".65rem",marginBottom:".75rem"}}>
                <div style={{width:36,height:36,borderRadius:"50%",background:"#1e2530",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"DM Mono",fontSize:".72rem",fontWeight:600}}>{initials(ticket.user?.username||"")}</div>
                <div>
                  <div style={{fontSize:".82rem",fontWeight:600}}>{ticket.user?.username}</div>
                  <div style={{fontSize:".7rem",color:"#5a6a7a"}}>{ticket.user?.email}</div>
                </div>
              </div>
              <div style={{fontSize:".72rem",color:"#5a6a7a",borderTop:"1px solid #1e2530",paddingTop:".65rem"}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:".3rem"}}>
                  <span>Catégorie</span><span style={{color:"#e8edf2"}}>{ticket.category}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:".3rem"}}>
                  <span>Créé le</span><span style={{color:"#e8edf2",fontFamily:"DM Mono"}}>{fmt(ticket.created_at)}</span>
                </div>
                <div style={{display:"flex",justifyContent:"space-between"}}>
                  <span>Résolu le</span><span style={{color:"#e8edf2",fontFamily:"DM Mono"}}>{ticket.resolved_at?fmt(ticket.resolved_at):"—"}</span>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/* ════ TABS ══════════════════════════════════════════════════════════════════ */

function OverviewTab({ stats, role, me }) {
  const s = stats || MOCK_STATS;
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG.staff;
  const entries = [];

  if(s.tickets_assigned!==undefined) entries.push({label:"Tickets assignés",val:s.tickets_assigned,c:"text"},{label:"Tickets ouverts",val:s.tickets_open,c:"gold"},{label:"Résolus aujourd'hui",val:s.tickets_resolved_today,c:"green"});
  if(s.pending_withdrawals!==undefined) entries.push({label:"Retraits en attente",val:s.pending_withdrawals,c:s.pending_withdrawals>0?"red":"text"});
  if(s.open_markets!==undefined) entries.push({label:"Marchés ouverts",val:s.open_markets,c:"accent"});
  if(s.total_users!==undefined) entries.push({label:"Utilisateurs",val:s.total_users,c:"text"},{label:"Nouveaux aujourd'hui",val:s.new_users_today,c:"green"});
  entries.push({label:"Mes actions aujourd'hui",val:s.my_actions_today,c:"textDim"});

  // Utiliser les vraies permissions de l'API si disponibles, sinon fallback sur les tabs
  const apiPerms  = me?.permissions || [];
  const showPerms = apiPerms.length > 0;

  // Libellés lisibles pour les permissions API
  const PERM_LABELS = {
    view_markets:       "Voir les marchés",
    view_users:         "Voir les utilisateurs",
    view_transactions:  "Voir les transactions",
    view_withdrawals:   "Voir les retraits",
    view_comments:      "Voir les commentaires",
    view_stats:         "Voir les statistiques",
    view_tickets:       "Voir les tickets",
    view_audit_log:     "Journal d'audit",
    delete_comment:     "Supprimer des commentaires",
    respond_tickets:    "Répondre aux tickets",
    flag_content:       "Signaler du contenu",
    approve_withdrawal: "Approuver des retraits",
    reject_withdrawal:  "Rejeter des retraits",
    create_market:      "Créer des marchés",
    edit_market:        "Modifier des marchés",
    publish_market:     "Publier des marchés",
    resolve_market:     "Résoudre des marchés",
    manage_users:       "Gérer les utilisateurs",
    assign_roles:       "Assigner des rôles",
  };

  return(
    <>
      <StatGrid>
        {entries.map((x,i)=><StatCard key={x.label} $i={i}><StatVal $c={x.c}>{x.val}</StatVal><StatLbl>{x.label}</StatLbl></StatCard>)}
      </StatGrid>

      <Panel>
        <PHead>
          <PTitle>Mes permissions — {me?.role_label || cfg.label}</PTitle>
          <span style={{fontFamily:"DM Mono,monospace",fontSize:".62rem",color:"#5a6a7a"}}>
            {showPerms ? `${apiPerms.length} permission${apiPerms.length>1?"s":""}` : `${cfg.tabs.length} onglet${cfg.tabs.length>1?"s":""}`}
          </span>
        </PHead>
        <div style={{padding:"1.25rem",display:"flex",flexWrap:"wrap",gap:".5rem"}}>
          {showPerms
            ? apiPerms.map(p=>(
                <div key={p} style={{display:"flex",alignItems:"center",gap:".4rem",
                  padding:".35rem .8rem",borderRadius:"6px",
                  background:"rgba(0,230,118,.06)",border:"1px solid rgba(0,230,118,.2)",
                  fontSize:".75rem",color:"#00e676"}}>
                  <span>✓</span>
                  {PERM_LABELS[p] || p.replaceAll("_"," ")}
                </div>
              ))
            : cfg.tabs.map(t=>(
                <div key={t} style={{display:"flex",alignItems:"center",gap:".4rem",
                  padding:".35rem .8rem",borderRadius:"6px",
                  background:"rgba(0,230,118,.06)",border:"1px solid rgba(0,230,118,.2)",
                  fontSize:".75rem",color:"#00e676"}}>
                  <span>✓</span>{TAB_LABELS[t]?.label}
                </div>
              ))
          }
        </div>
      </Panel>
    </>
  );
}

function TicketsTab({ toast, onOpenTicket }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  const[filter,setFilter]=useState("ALL");const[search,setSearch]=useState("");
  useEffect(()=>{empGetTickets({status:filter!=="ALL"?filter:undefined}).then(r=>setItems(r.data)).catch(()=>setItems(MOCK_TICKETS)).finally(()=>setLoading(false));},[filter]);
  const displayed=items.filter(t=>!search||t.subject.toLowerCase().includes(search.toLowerCase())||t.user.toLowerCase().includes(search.toLowerCase()));
  const COLS="24px 2fr 90px 80px 80px 120px 100px";
  return(
    <Panel>
      <PHead>
        <PTitle>Tickets ({items.length})</PTitle>
        <PActions>
          <Search placeholder="Rechercher…" value={search} onChange={e=>setSearch(e.target.value)}/>
          {["ALL","open","in_progress","resolved","closed"].map(f=><FilterChip key={f} $active={filter===f} onClick={()=>setFilter(f)}>{f==="ALL"?"Tous":f}</FilterChip>)}
        </PActions>
      </PHead>
      <THead $cols={COLS}><TH/><TH>Sujet</TH><TH>Catégorie</TH><TH>Priorité</TH><TH>Statut</TH><TH>Utilisateur</TH><TH>Créé</TH></THead>
      {loading?Array.from({length:4}).map((_,i)=><Skel key={i}/>):
       displayed.length===0?<Empty>Aucun ticket.</Empty>:
       displayed.map(t=>(
        <TRow key={t.id} $cols={COLS} $click onClick={()=>onOpenTicket(t.id)}>
          <Cell><PriorityDot $p={t.priority}/></Cell>
          <Cell style={{whiteSpace:"normal",lineHeight:1.3,fontSize:".75rem"}}>{t.subject}</Cell>
          <Cell $dim>{t.category}</Cell>
          <Cell><Pill $s={t.priority}>{t.priority}</Pill></Cell>
          <Cell><Pill $s={t.status}>{t.status}</Pill></Cell>
          <Cell $mono>{t.user}</Cell>
          <Cell $dim style={{fontSize:".7rem"}}>{timeAgo(t.created_at)}</Cell>
        </TRow>
      ))}
    </Panel>
  );
}

function ModerationTab({ toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  const[acting,setActing]=useState(null);
  useEffect(()=>{empGetComments().then(r=>setItems(r.data)).catch(()=>setItems(MOCK_COMMENTS)).finally(()=>setLoading(false));},[]);
  const del=async(id)=>{setActing(id);try{await empDeleteComment(id);}catch{}setItems(p=>p.filter(x=>x.id!==id));toast.ok("Commentaire supprimé.");setActing(null);};
  const COLS="120px 1fr 180px 90px 80px";
  return(
    <Panel>
      <PHead><PTitle>Commentaires ({items.length})</PTitle></PHead>
      <THead $cols={COLS}><TH>Auteur</TH><TH>Contenu</TH><TH>Marché</TH><TH>Date</TH><TH>Action</TH></THead>
      {loading?Array.from({length:3}).map((_,i)=><Skel key={i}/>):
       items.length===0?<Empty>Aucun commentaire.</Empty>:
       items.map(c=>(
        <TRow key={c.id} $cols={COLS}>
          <Cell $mono>{c.author}</Cell>
          <Cell $dim style={{whiteSpace:"normal",fontSize:".74rem",lineHeight:1.3}}>{c.content}</Cell>
          <Cell $dim style={{fontSize:".7rem",whiteSpace:"normal"}}>{c.market}</Cell>
          <Cell $dim style={{fontSize:".7rem"}}>{timeAgo(c.created_at)}</Cell>
          <Cell><Btn $t="danger" disabled={acting===c.id} onClick={()=>del(c.id)}>{acting===c.id?"…":"✕ Suppr."}</Btn></Cell>
        </TRow>
      ))}
    </Panel>
  );
}

function WithdrawalsTab({ toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  const[filter,setFilter]=useState("PENDING");const[acting,setActing]=useState(null);
  const[rejectId,setRejectId]=useState(null);const[reason,setReason]=useState("");
  useEffect(()=>{empGetWithdrawals({status:filter!=="ALL"?filter:undefined}).then(r=>setItems(r.data)).catch(()=>setItems(MOCK_WITHDRAWALS.filter(w=>filter==="ALL"||w.status===filter))).finally(()=>setLoading(false));},[filter]);
  const approve=async(id)=>{setActing(id);try{await empApproveWithdrawal(id);}catch{}setItems(p=>p.map(w=>w.id===id?{...w,status:"APPROVED"}:w));toast.ok("Retrait approuvé.");setActing(null);};
  const reject=async(id)=>{if(!reason.trim())return;setActing(id);try{await empRejectWithdrawal(id,{reason});}catch{}setItems(p=>p.map(w=>w.id===id?{...w,status:"REJECTED"}:w));toast.err("Retrait rejeté.");setRejectId(null);setReason("");setActing(null);};
  const COLS="100px 80px 90px 1fr 80px 90px";
  return(
    <Panel>
      <PHead>
        <PTitle>Retraits</PTitle>
        <PActions>{["ALL","PENDING","APPROVED","REJECTED"].map(f=><FilterChip key={f} $active={filter===f} onClick={()=>setFilter(f)}>{f}</FilterChip>)}</PActions>
      </PHead>
      <THead $cols={COLS}><TH>Utilisateur</TH><TH>Coin</TH><TH>Montant</TH><TH>Adresse</TH><TH>Statut</TH><TH>Actions</TH></THead>
      {loading?Array.from({length:3}).map((_,i)=><Skel key={i}/>):
       items.length===0?<Empty>Aucun retrait {filter.toLowerCase()}.</Empty>:
       items.map(w=>(
        <React.Fragment key={w.id}>
          <TRow $cols={COLS}>
            <Cell $mono>{w.user}</Cell>
            <Cell $mono>{w.coin}</Cell>
            <Cell $mono>${parseFloat(w.amount).toFixed(2)}</Cell>
            <Cell $dim style={{fontSize:".7rem"}}>{w.address}</Cell>
            <Cell><Pill $s={w.status}>{w.status}</Pill></Cell>
            <Cell>
              {w.status==="PENDING"?(
                <BtnRow>
                  <Btn $t="ok" disabled={acting===w.id} onClick={()=>approve(w.id)}>✓</Btn>
                  <Btn $t="danger" onClick={()=>setRejectId(rejectId===w.id?null:w.id)}>✕</Btn>
                </BtnRow>
              ):<span style={{fontSize:".7rem",color:"#5a6a7a"}}>—</span>}
            </Cell>
          </TRow>
          {rejectId===w.id&&(
            <div style={{padding:".5rem 1.25rem",background:"#0a0c0e",borderBottom:"1px solid #1e2530",display:"flex",gap:".5rem",alignItems:"center"}}>
              <input autoFocus placeholder="Motif du rejet…" value={reason} onChange={e=>setReason(e.target.value)} onKeyDown={e=>e.key==="Enter"&&reject(w.id)} style={{flex:1,background:"#080a0c",border:"1px solid #1e2530",borderRadius:"6px",color:"#e8edf2",fontFamily:"Syne,sans-serif",fontSize:".75rem",padding:".35rem .7rem",outline:"none"}}/>
              <Btn $t="danger" disabled={!reason.trim()||acting===w.id} onClick={()=>reject(w.id)}>Confirmer</Btn>
            </div>
          )}
        </React.Fragment>
      ))}
    </Panel>
  );
}

const PAYOUT_METHODS = { mobile:{icon:"📱",label:"Mobile"}, bank:{icon:"🏦",label:"Banque"}, paypal:{icon:"🅿️",label:"PayPal"} };

function PayoutsTab({ toast }) {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  const[statusF,setStatusF]=useState("PENDING");const[methodF,setMethodF]=useState("ALL");
  const[acting,setActing]=useState(null);
  const[panel,setPanel]=useState(null);   // { id, type:"complete" | "reject" }
  const[text,setText]=useState("");

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const params={};
      if(statusF!=="ALL")params.status=statusF;
      if(methodF!=="ALL")params.method=methodF;
      const r=await empGetPayouts(params);
      setItems(r.data.results||r.data);
    }catch(e){toast.err(e?.response?.data?.detail||"Impossible de charger les payouts.");setItems([]);}
    finally{setLoading(false);}
  },[statusF,methodF]);
  useEffect(()=>{load();},[load]);

  const run=async(p,action,body={})=>{
    setActing(p.id);
    try{
      await empPayoutAction(p.id,action,body);
      toast.ok(action==="approve"?"Demande approuvée."
        :action==="complete"?"Paiement enregistré — l'utilisateur est notifié."
        :"Demande rejetée — fonds remboursés, utilisateur notifié.");
      setPanel(null);setText("");load();
    }catch(e){toast.err(e?.response?.data?.detail||"Action échouée.");}
    finally{setActing(null);}
  };

  const COLS="100px 90px 90px 1fr 100px 110px 210px";
  const inputStyle={flex:1,background:"#080a0c",border:"1px solid #1e2530",borderRadius:"6px",color:"#e8edf2",fontFamily:"Syne,sans-serif",fontSize:".75rem",padding:".35rem .7rem",outline:"none"};
  return(
    <Panel>
      <PHead>
        <PTitle>Payouts ({items.length})</PTitle>
        <PActions>
          {["ALL","PENDING","APPROVED","COMPLETED","REJECTED"].map(f=><FilterChip key={f} $active={statusF===f} onClick={()=>setStatusF(f)}>{f}</FilterChip>)}
          <span style={{width:1,height:16,background:"#1e2530"}}/>
          {["ALL","mobile","bank","paypal"].map(f=><FilterChip key={f} $active={methodF===f} onClick={()=>setMethodF(f)}>{f==="ALL"?"TOUS":PAYOUT_METHODS[f].icon+" "+PAYOUT_METHODS[f].label}</FilterChip>)}
        </PActions>
      </PHead>
      <div style={{overflowX:"auto"}}><div style={{minWidth:820}}>
        <THead $cols={COLS}><TH>Utilisateur</TH><TH>Méthode</TH><TH>Montant</TH><TH>Destination</TH><TH>Statut</TH><TH>Date</TH><TH>Actions</TH></THead>
        {loading?Array.from({length:3}).map((_,i)=><Skel key={i}/>):
         items.length===0?<Empty>Aucun payout {statusF==="ALL"?"":statusF.toLowerCase()}.</Empty>:
         items.map(p=>{
          const st=String(p.status).toUpperCase();
          const m=PAYOUT_METHODS[p.method]||{icon:"💳",label:p.method};
          const open=st==="PENDING"||st==="APPROVED";
          return(
            <React.Fragment key={p.id}>
              <TRow $cols={COLS}>
                <Cell $mono title={p.user_email}>{p.user}</Cell>
                <Cell>{m.icon} {m.label}</Cell>
                <Cell $mono>${parseFloat(p.amount_usd).toFixed(2)}</Cell>
                <Cell $dim style={{fontSize:".7rem"}} title={p.destination}>{p.destination}</Cell>
                <Cell><Pill $s={st}>{st}</Pill></Cell>
                <Cell $dim style={{fontSize:".7rem"}}>{timeAgo(p.created_at)}</Cell>
                <Cell>
                  {open?(
                    <BtnRow>
                      {st==="PENDING"&&<Btn $t="ok" disabled={acting===p.id} onClick={()=>run(p,"approve")}>✓ Approuver</Btn>}
                      <Btn $t="ok" disabled={acting===p.id} onClick={()=>{setPanel(panel?.id===p.id&&panel.type==="complete"?null:{id:p.id,type:"complete"});setText("");}}>💸 Payé</Btn>
                      <Btn $t="danger" disabled={acting===p.id} onClick={()=>{setPanel(panel?.id===p.id&&panel.type==="reject"?null:{id:p.id,type:"reject"});setText("");}}>✕</Btn>
                    </BtnRow>
                  ):<span style={{fontSize:".7rem",color:"#5a6a7a"}}>—</span>}
                </Cell>
              </TRow>
              {panel?.id===p.id&&(
                <div style={{padding:".5rem 1.25rem",background:"#0a0c0e",borderBottom:"1px solid #1e2530",display:"flex",gap:".5rem",alignItems:"center"}}>
                  {panel.type==="complete"?(<>
                    <input autoFocus placeholder="Référence du paiement (ex. ID transaction PayPal / Mobile Money) — optionnel" value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&run(p,"complete",{payout_reference:text.trim()})} style={inputStyle}/>
                    <Btn $t="ok" disabled={acting===p.id} onClick={()=>run(p,"complete",{payout_reference:text.trim()})}>Confirmer le paiement</Btn>
                  </>):(<>
                    <input autoFocus placeholder="Motif du rejet (communiqué à l'utilisateur)…" value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&text.trim()&&run(p,"reject",{reason:text.trim()})} style={inputStyle}/>
                    <Btn $t="danger" disabled={!text.trim()||acting===p.id} onClick={()=>run(p,"reject",{reason:text.trim()})}>Confirmer le rejet</Btn>
                  </>)}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div></div>
    </Panel>
  );
}

function UsersTab() {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  const[search,setSearch]=useState("");
  useEffect(()=>{empGetUsers().then(r=>setItems(r.data)).catch(()=>setItems(MOCK_USERS)).finally(()=>setLoading(false));},[]);
  const displayed=items.filter(u=>!search||u.username?.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase()));
  const COLS="130px 1fr 90px 90px 80px";
  return(
    <Panel>
      <PHead>
        <PTitle>Utilisateurs ({items.length})</PTitle>
        <PActions><Search placeholder="Rechercher…" value={search} onChange={e=>setSearch(e.target.value)}/></PActions>
      </PHead>
      <THead $cols={COLS}><TH>Username</TH><TH>Email</TH><TH>Balance</TH><TH>Inscrit le</TH><TH>Statut</TH></THead>
      {loading?Array.from({length:4}).map((_,i)=><Skel key={i}/>):
       displayed.length===0?<Empty>Aucun utilisateur trouvé.</Empty>:
       displayed.map(u=>(
        <TRow key={u.id} $cols={COLS}>
          <Cell $mono>{u.username}</Cell>
          <Cell $dim style={{fontSize:".72rem"}}>{u.email||"—"}</Cell>
          <Cell $mono style={{color:parseFloat(u.balance||0)>0?"#00e676":"inherit"}}>{u.balance?`$${u.balance}`:"—"}</Cell>
          <Cell $dim>{fmt(u.date_joined)}</Cell>
          <Cell><Pill $s={u.is_active?"active":"inactive"}>{u.is_active?"Actif":"Inactif"}</Pill></Cell>
        </TRow>
      ))}
    </Panel>
  );
}

function ActivityTab() {
  const [items,setItems]=useState([]);const[loading,setLoading]=useState(true);
  useEffect(()=>{empGetActivity().then(r=>setItems(r.data)).catch(()=>setItems([
    {id:1,employee:"moi",action:"ticket_message_sent",target_type:"ticket",target_id:"t2",created_at:"2026-03-17T10:30:00Z"},
    {id:2,employee:"moi",action:"ticket_self_assigned",target_type:"ticket",target_id:"t1",created_at:"2026-03-17T09:00:00Z"},
    {id:3,employee:"moi",action:"withdrawal_approved",target_type:"withdrawal",target_id:"w1",created_at:"2026-03-17T08:15:00Z"},
  ])).finally(()=>setLoading(false));},[]);
  const COLS="1fr 100px 100px 100px";
  return(
    <Panel>
      <PHead><PTitle>Journal d'activité</PTitle></PHead>
      <THead $cols={COLS}><TH>Action</TH><TH>Type</TH><TH>Cible</TH><TH>Date</TH></THead>
      {loading?Array.from({length:4}).map((_,i)=><Skel key={i}/>):
       items.length===0?<Empty>Aucune activité.</Empty>:
       items.map(l=>(
        <TRow key={l.id} $cols={COLS}>
          <Cell style={{fontSize:".77rem"}}>{l.action.replaceAll("_"," ")}</Cell>
          <Cell $dim>{l.target_type}</Cell>
          <Cell $mono style={{fontSize:".7rem"}}>{String(l.target_id).slice(0,10)}</Cell>
          <Cell $dim style={{fontSize:".7rem"}}>{timeAgo(l.created_at)}</Cell>
        </TRow>
      ))}
    </Panel>
  );
}

/* ════ ROOT ══════════════════════════════════════════════════════════════════ */
export default function EmployeeDashboard({ user, onLogout, onHome }) {
  const [me,      setMe]      = useState(null);
  const [stats,   setStats]   = useState(null);
  const [tab,     setTab]     = useState("overview");
  const [ticketId,setTicketId]= useState(null);
  const [sideOpen,  setSideOpen]  = useState(false);  // mobile
  const [collapsed, setCollapsed] = useState(false);  // desktop
  const toast = useToasts();

  const handleTab = (id) => { setTab(id); setSideOpen(false); setTicketId(null); };

  useEffect(()=>{
    empGetMe().then(r=>setMe(r.data)).catch(()=>setMe({
      employee_id:"EMP-00001",username:user?.username||"employee",
      role:"support",role_label:"Support Client",
      department:"Service Client",status:"active",
      permissions:["view_tickets","respond_tickets","view_users"],
    }));
    empGetStats().then(r=>setStats(r.data)).catch(()=>setStats(MOCK_STATS));
  },[]);

  // Compteurs de la barre latérale : rafraîchis toutes les 30 s, au retour sur l'onglet,
  // et immédiatement quand une alerte (nouveau ticket, réponse, contact) arrive en temps réel.
  const refreshStats = useCallback(()=>{ empGetStats().then(r=>setStats(r.data)).catch(()=>{}); },[]);
  useEffect(()=>{
    const id = setInterval(refreshStats, 30000);
    const onVis = () => { if(!document.hidden) refreshStats(); };
    document.addEventListener("visibilitychange", onVis);
    return ()=>{ clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  },[refreshStats]);
  useNotificationSocket(user, (n)=>{
    const type = n?.notif_type || n?.category || "";
    if(["ticket_support_alert","contact_support_alert","ticket_created","ticket_reply"].includes(type)){
      refreshStats();
      if(n.title) toast.info(`🎫 ${n.title}${n.body?` — ${n.body}`:""}`);
    }
  });

  const role    = me?.role || "support";
  const cfg     = ROLE_CONFIG[role] || ROLE_CONFIG.staff;
  const tabs    = cfg.tabs;

  const tabHasNotif = t => {
    if(t==="tickets"&&(stats?.tickets_open||0)>0) return stats.tickets_open;
    if(t==="withdrawals"&&(stats?.pending_withdrawals||0)>0) return stats.pending_withdrawals;
    if(t==="payouts"&&(stats?.pending_payouts||0)>0) return stats.pending_payouts;
    return null;
  };

  // Shared sidebar content
  const sidebarContent = (
    <Sidebar $open={sideOpen} $collapsed={collapsed}>
      <SideTop>
        <SideLogo onClick={onHome}>EVEN<span>M</span>T</SideLogo>
        <RoleBadge $color={cfg.color}>{cfg.icon} {cfg.label}</RoleBadge>
      </SideTop>
      <Nav>
        {tabs.map(t=>(
          <NavItem key={t} $active={tab===t} onClick={()=>handleTab(t)}>
            <NavIcon>{TAB_LABELS[t].icon}</NavIcon>
            {TAB_LABELS[t].label}
            {tabHasNotif(t)&&<NavBadge>{tabHasNotif(t)}</NavBadge>}
          </NavItem>
        ))}
      </Nav>
      <SideFoot>
        <UserChip>
          <Avatar $color={cfg.color}>{initials(me?.username||"E")}</Avatar>
          <div>
            <div style={{fontWeight:600,fontSize:".8rem"}}>{me?.username||user?.username}</div>
            <div style={{fontSize:".62rem",color:"#5a6a7a"}}>{me?.employee_id}</div>
          </div>
        </UserChip>
        {onLogout&&<SignOut onClick={onLogout}>Déconnexion</SignOut>}
      </SideFoot>
    </Sidebar>
  );

  const toggleBtn = (
    <ToggleBtn
      $collapsed={collapsed} $open={sideOpen}
      onClick={()=>{ if(window.innerWidth<=768) setSideOpen(o=>!o); else setCollapsed(c=>!c); }}
      title={collapsed||!sideOpen?"Ouvrir le menu":"Fermer le menu"}
    >
      {(sideOpen||!collapsed)?"‹":"›"}
    </ToggleBtn>
  );

  // Ticket detail mode
  if(ticketId) return(
    <ThemeProvider theme={theme}>
      <Shell>
        {toggleBtn}
        <SideOverlay $open={sideOpen} onClick={()=>setSideOpen(false)}/>
        {sidebarContent}
        <Main style={{paddingTop:"3.5rem"}}>
          <TicketDetail ticketId={ticketId} onBack={()=>setTicketId(null)} toast={toast} empRole={role}/>
        </Main>
        <ToastWrap>{toast.list.map(t=><ToastItem key={t.id} $t={t.t}>{t.msg}</ToastItem>)}</ToastWrap>
      </Shell>
    </ThemeProvider>
  );

  return(
    <ThemeProvider theme={theme}>
      <Shell>
        {toggleBtn}
        <SideOverlay $open={sideOpen} onClick={()=>setSideOpen(false)}/>
        {sidebarContent}
        <Main style={{paddingTop:"3.5rem"}}>
          {tab==="overview"    && <><PageTitle>Bonjour <span>{me?.username||user?.username}</span> 👋</PageTitle><PageSub>{cfg.label} — {me?.department||"EVPTrading"}</PageSub><OverviewTab stats={stats} role={role} me={me}/></>}
          {tab==="tickets"     && <><PageTitle>Tickets <span>Support</span></PageTitle><PageSub>Gérez les demandes des utilisateurs — vue employé</PageSub><EmployeeSupportTickets empUser={me} toast={toast}/></>}
          {tab==="moderation"  && <><PageTitle>Modération <span>Commentaires</span></PageTitle><PageSub>Superviser et modérer le contenu de la plateforme</PageSub><ModerationTab toast={toast}/></>}
          {tab==="withdrawals" && <><PageTitle>Retraits <span>en attente</span></PageTitle><PageSub>Approuver ou rejeter les demandes de retrait</PageSub><WithdrawalsTab toast={toast}/></>}
          {tab==="payouts"     && <><PageTitle>Payouts <span>Mobile · Banque · PayPal</span></PageTitle><PageSub>Valider, payer ou rejeter — l'utilisateur est notifié par email, push et dans son historique wallet</PageSub><PayoutsTab toast={toast}/></>}
          {tab==="users"       && <><PageTitle>Utilisateurs <span>(lecture)</span></PageTitle><PageSub>Consulter les informations des utilisateurs</PageSub><UsersTab/></>}
          {tab==="activity"    && <><PageTitle>Mon <span>Activité</span></PageTitle><PageSub>Historique de vos actions sur la plateforme</PageSub><ActivityTab/></>}
        </Main>
        <ToastWrap>{toast.list.map(t=><ToastItem key={t.id} $t={t.t}>{t.msg}</ToastItem>)}</ToastWrap>
      </Shell>
    </ThemeProvider>
  );
}
